import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "flicket-ui";
import { AnimatePresence } from "framer-motion";
import { useRouter } from "next/router";
import { ContentModal, Layout, Seo, Status } from "~components";
import {
  Actions,
  FilterModal,
  Filters,
  MobileFilters,
  Options,
  SeatsIO,
} from "~components/reservation";
import { MobileOptions } from "~components/reservation/options/options.MobileLayout";
import { Placeholder } from "~components/reservation/options/options.Placeholder";
import { TicketDetailsModal } from "~features/seated-reservation/components/common/modal.TicketDetails";
import { LandingPage, VenueSeating } from "~graphql/sdk";
import {
  useAccount,
  useIsMobile,
  useIsMounted,
  useOrganization,
  useReservation,
} from "~hooks";
import { showToast } from "~lib";
import { useAtom } from "jotai";
import { googleAnalyticsMembershipAtom } from "~hooks/useGoogleAnalyticsPageView";
import { MembershipVenue } from "~features/reservation/reservation-types";
import { NonSeatedMembershipReservation } from "~features/nonseated-reservation/components/NonSeatedMembershipReservation";
import { initialState } from "~features/seated-reservation/store";
import EventHeader from "~components/common/common.EventHeader";

const MembershipReservation = () => {
  const { organization, hasFeature, t } = useOrganization();
  const [isNavigating, setNavigating] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [onAddError, setOnAddError] = useState("");
  const isMobile = useIsMobile();
  const isMounted = useIsMounted();
  const router = useRouter();
  const [showOptions, setShowOptions] = useState(!router?.query?.ownedTickets);
  const { isAdmin } = useAccount();
  const [eventVenues, setEventVenues] = useState<MembershipVenue>([]);
  const [locations, setLocations] = useState("");
  const {
    chart,
    membership,
    error,
    isLoading,
    handleOrderCreation,
    options,
    selectableZones,
    updateOptions,
    getPrice,
    changeTickets,
    isLoadingZones,
    priceComp,
    seatAmountOptions,
  } = useReservation("membership", router.query.membershipId as string);

  const isInactive = membership?.isActive === false;

  useEffect(() => {
    if (isInactive) {
      void router.push("/memberships");
    }
  }, [isInactive]);

  const [, setMembership] = useAtom(googleAnalyticsMembershipAtom);

  useEffect(() => {
    if (membership) {
      //  @ts-expect-error atom type issue while using strict:false
      setMembership(membership);
    }
  }, [membership]);

  useEffect(() => {
    void router.prefetch("/checkout/[orderId]");
  }, []);

  const changeCoverType = (value: string) => updateOptions("coverType", value);
  const changeZoneType = (value: string) => updateOptions("zoneType", value);
  const changeSort = (value: string) => updateOptions("selectedSort", value);
  const setAmount = (value: number) => updateOptions("amount", value);
  const setZone = (value: string) => updateOptions("selectedZone", value);
  const setSection = (value: string) => updateOptions("selectedSection", value);
  const changeSeatSelect = (value: string) =>
    updateOptions("seatSelectType", value);

  useEffect(() => {
    if (
      !options?.validSeatSelection &&
      options?.amount >= 0 &&
      membership?.venue?.seating === VenueSeating.Seated
    ) {
      showToast(
        "Invalid seat selection. You have left a single seat empty",
        "error"
      );
    }
  }, [options?.validSeatSelection]);

  const closeOptions = useCallback(() => setShowOptions(false), [
    setShowOptions,
  ]);

  const onNavigateCheckout = useCallback(async () => {
    if (isNavigating) return;

    setNavigating(true);

    const wasSuccessful = await handleOrderCreation((errorMessage) => {
      setOnAddError(errorMessage);
      setIsErrorModalOpen(true);
      setNavigating(false);
      return;
    });

    if (!wasSuccessful) {
      setNavigating(false);
    }
  }, [handleOrderCreation]);

  const handleClose = () => {
    if (!options?.amount) {
      setAmount(initialState.options.amount);
    }

    closeOptions();
  };

  useEffect(() => {
    membership?.events.forEach((membershipEvent) => {
      const eventVenueList = eventVenues;
      eventVenueList.push(membershipEvent.venue);

      const newlist = Array.from(
        new Set(eventVenueList.map((venue) => venue.name))
      ).map((name) => {
        return {
          name: name,
          address: eventVenueList.find((venue) => venue.name === name).address,
          timezone: eventVenueList.find((venue) => venue.name === name)
            .timezone,
          locale: eventVenueList.find((venue) => venue.name === name).locale,
        };
      });
      setEventVenues(newlist);
      setLocations(
        newlist
          .map((venue) => `${venue.name}, ${venue?.address?.city}`)
          .join(", ")
      );
    });
  }, [membership]);

  const memoizedOptions = useMemo(
    () => (
      <AnimatePresence>
        <FilterModal
          isOpen={isMounted && showOptions && !isMobile}
          onClose={handleClose}
        >
          <Options
            amount={options?.amount}
            handleClose={handleClose}
            setAmount={setAmount}
            title={membership?.name}
            startDate={membership?.startDate}
            endDate={membership?.endDate}
            location={locations}
            seatAmountOptions={seatAmountOptions}
            filterOptions={membership?.venue?.filterOptions}
          />
        </FilterModal>
      </AnimatePresence>
    ),
    [
      options?.coverType,
      options?.zoneType,
      options?.selectedSort,
      options?.amount,
      closeOptions,
      changeCoverType,
      changeZoneType,
      changeSort,
      setAmount,
      isMobile,
      seatAmountOptions,
    ]
  );

  //For seated memberships
  const memoizedActions = useMemo(
    () => (
      <Actions
        eventOrMembership={membership}
        type="membership"
        addons={membership?.addons}
        changeTickets={changeTickets}
        changingSeatsFee={membership?.changingSeatsFee}
        venueName={membership?.venue?.name}
        zones={selectableZones}
        currentZone={options?.selectedZone}
        setCurrentZone={setZone}
        validSeatSelection={options?.validSeatSelection}
        currentSort={options?.selectedSort}
        currentSection={options?.selectedSection}
        setCurrentSection={setSection}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        navigateCheckout={onNavigateCheckout}
        isNavigating={isNavigating}
        getPrice={getPrice}
        priceComp={priceComp}
      />
    ),
    [
      membership?.addons,
      membership?.venue?.name,
      membership?.changingSeatsFee,
      changeTickets,
      selectableZones,
      setZone,
      setSection,
      options?.selectedSort,
      options?.selectedZone,
      options?.selectedSection,
      onNavigateCheckout,
      isNavigating,
      getPrice,
      priceComp,
      options?.validSeatSelection,
    ]
  );

  const memoizedSeatsIO = useMemo(
    () => <SeatsIO showOverlay={showOptions} chart={chart} />,
    [showOptions, chart]
  );

  const loading = isLoading || !membership?.venue?.seating || isInactive;

  if (
    !loading &&
    !hasFeature("LegacyGATicketSelection") &&
    membership?.venue?.seating !== VenueSeating.Seated
  ) {
    return (
      <>
        <Seo
          title={`Buy ${t("membership_name")} - ${membership?.name}`}
          description={undefined}
        />
        <NonSeatedMembershipReservation
          membership={membership}
          isMembership
          isAdmin={isAdmin}
        />
      </>
    );
  }

  return (
    <>
      <Seo
        title={`Buy ${t("membership_name")} - ${membership?.name}`}
        description={undefined}
      />
      <Status error={error} loading={loading}>
        <EventHeader
          type="membership"
          title={membership?.name}
          startDate={membership?.startDate}
          endDate={membership?.endDate}
          venue={membership?.venue}
          location={locations}
          accessibility={membership?.accessibility}
          image={membership?.banner}
          description={membership?.description}
          supportInfo={organization?.supportInfo}
          goBack={() =>
            void router.push(
              organization?.landingPage === LandingPage.SingleEvent
                ? "/"
                : "/memberships"
            )
          }
          coordinates={`${membership?.venue?.location?.lat},${membership?.venue?.location?.lng}`}
        />
        <Layout layoutType="sub">
          {membership?.venue?.seating !== VenueSeating.Seated ? (
            <NonSeatedMembershipReservation
              membership={membership}
              isMembership
              isAdmin={isAdmin}
            />
          ) : (
            <>
              <Filters
                amount={options?.amount}
                setAmount={setAmount}
                amountDisabled={!!changeTickets?.length}
                coverType={options?.coverType}
                setCoverType={changeCoverType}
                alcoholFreeZone={options?.zoneType}
                setAlcoholFreeZone={changeZoneType}
                selectedSort={options?.selectedSort}
                setSelectedSort={changeSort}
                seatSelectType={options?.seatSelectType}
                setSeatSelectType={changeSeatSelect}
                seatAmountOptions={seatAmountOptions}
                filterOptions={membership?.venue?.filterOptions}
              />
              <Flex
                justifyContent={["flex-end", "flex-start"]}
                overflow="hidden"
                position="relative"
                height={
                  showOptions
                    ? {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                      }
                    : {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                        md: isAdmin
                          ? "calc(100vh - 169px)"
                          : "calc(100vh - 122px)",
                      }
                }
                maxHeight={
                  showOptions
                    ? {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                      }
                    : {
                        _: isAdmin
                          ? "calc(100vh - 96px)"
                          : "calc(100vh - 64px)",
                        md: isAdmin
                          ? "calc(100vh - 169px)"
                          : "calc(100vh - 122px)",
                      }
                }
              >
                <Flex w="100%" maxW="100%">
                  {!isMobile && (
                    <>
                      <Box
                        position="relative"
                        d={{ _: "none", md: "block" }}
                        width="100%"
                        minW={{ _: "100%", md: "256px" }}
                        maxW={{ _: "100%", md: "403px" }}
                      >
                        {isLoadingZones ||
                        typeof selectableZones === "undefined" ? (
                          <Placeholder />
                        ) : (
                          memoizedActions
                        )}
                      </Box>
                      {showOptions && memoizedOptions}
                    </>
                  )}
                  <Box position="relative" maxW="100%" width="100%" bg="N100">
                    {memoizedSeatsIO}
                    <MobileFilters
                      amount={options?.amount}
                      setAmount={setAmount}
                      amountDisabled={!!changeTickets?.length}
                      coverType={options?.coverType}
                      setCoverType={changeCoverType}
                      alcoholFreeZone={options?.zoneType}
                      setAlcoholFreeZone={changeZoneType}
                      selectedSort={options?.selectedSort}
                      setSelectedSort={changeSort}
                      seatSelectType={options?.seatSelectType}
                      setSeatSelectType={changeSeatSelect}
                      seatAmountOptions={seatAmountOptions}
                      filterOptions={membership?.venue?.filterOptions}
                    />
                    {isLoadingZones ||
                    typeof selectableZones === "undefined" ? (
                      <Placeholder />
                    ) : (
                      memoizedActions
                    )}
                  </Box>
                </Flex>
                <MobileOptions showOptions={showOptions}>
                  <Options
                    amount={options?.amount}
                    handleClose={handleClose}
                    setAmount={setAmount}
                    seatAmountOptions={seatAmountOptions}
                    filterOptions={membership?.venue?.filterOptions}
                  />
                </MobileOptions>
              </Flex>
            </>
          )}
          <TicketDetailsModal />
        </Layout>

        <ContentModal
          isOpen={isErrorModalOpen}
          setIsOpen={() => setIsErrorModalOpen(false)}
          title="There was a problem..."
        >
          <div dangerouslySetInnerHTML={{ __html: onAddError }}></div>
        </ContentModal>
      </Status>
    </>
  );
};

export default MembershipReservation;
